"use client";

import React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Shield, Clock, Coins, UserCheck } from "lucide-react";
const comparisonData = [{
  type: "Term Life Protection",
  duration: "10 to 30 Years",
  cashValue: "Zero Accumulation",
  costVariant: "outline" as const,
  livingBenefits: "Company Benefits through your job; Death Benefit Only.\nDepending on Carrier; limited accelerated options, typically only terminal illness provided.\nOur Carriers: Full Accelerated Death Benefit riders for Chronic, Critical, and Terminal Illness Care."
}, {
  type: "Whole Life Assets",
  duration: "Lifetime",
  cashValue: "Guaranteed Fixed",
  costVariant: "secondary" as const,
  livingBenefits: "Limited access to cash value through loans; fixed accelerated benefit options.\nOur Carriers: Full Accelerated Death Benefit riders for Chronic, Critical, and Terminal Illness Care."
}, {
  type: "Index Universal Life",
  duration: "Flexible Lifetime",
  cashValue: "Market-Linked Growth",
  costVariant: "default" as const,
  livingBenefits: "Full Accelerated Death Benefit riders for chronic, critical, or terminal illness care."
}];
export default function TermVsPermanentInsurance() {
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: tableRef,
    isVisible: isTableVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="76364" id="coverage-comparison" className="dark bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        
        <div ref={introRef} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-12 md:mb-16 transition-all duration-700 ease-out ${isIntroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="md:col-span-5 flex flex-col justify-center">
            <h2 className="text-3xl md:text-5xl font-bold font-serif tracking-tight mb-4 text-foreground">
              Precision Coverage <br /><span className="text-primary italic">Comparison</span>
            </h2>
            <div className="h-1 w-20 bg-gradient-to-r from-primary to-primary/40 rounded-full mb-6"></div>
          </div>
          <div className="md:col-span-7 flex flex-col justify-center">
            <p className="text-lg text-muted-foreground leading-relaxed font-sans">
              At WealthCraft, we believe in a three-dimensional outlook on finance. There is no one-size-fits-all solution. Whether you seek affordable protection for a fixed period or a creative wealth-building tool with living benefits, comparing these structures is essential for your self-made pension.
            </p>
          </div>
        </div>

        
        <div ref={tableRef} className={`mt-10 rounded-xl border border-border bg-card/50 backdrop-blur-sm shadow-2xl overflow-hidden transition-all duration-700 delay-200 ease-out ${isTableVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Table>
            <TableHeader className="bg-card/80 backdrop-blur-md">
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="w-[250px] sticky top-0 bg-card/80 backdrop-blur-md z-10 py-6">
                  <div className="flex items-center gap-2 font-bold font-serif text-foreground uppercase tracking-wider text-xs">
                    <Shield className="w-4 h-4 text-primary" />
                    Policy Vehicle
                  </div>
                </TableHead>
                <TableHead className="sticky top-0 bg-card/80 backdrop-blur-md z-10 py-6">
                  <div className="flex items-center gap-2 font-bold font-serif text-foreground uppercase tracking-wider text-xs">
                    <Clock className="w-4 h-4 text-primary" />
                    Duration
                  </div>
                </TableHead>
                <TableHead className="sticky top-0 bg-card/80 backdrop-blur-md z-10 py-6">
                  <div className="flex items-center gap-2 font-bold font-serif text-foreground uppercase tracking-wider text-xs">
                    <Coins className="w-4 h-4 text-primary" />
                    Cash Value
                  </div>
                </TableHead>
                <TableHead className="sticky top-0 bg-card/80 backdrop-blur-md z-10 py-6 min-w-[300px]">
                  <div className="flex items-center gap-2 font-bold font-serif text-foreground uppercase tracking-wider text-xs">
                    <UserCheck className="w-4 h-4 text-accent-foreground" />
                    Living Benefits
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {comparisonData.map((row, i) => <TableRow key={row.type} data-index={i} className="group border-border hover:bg-white/5 transition-colors">
                  <TableCell className="font-bold py-8 font-serif text-lg">
                    <div className="flex flex-col gap-2">
                      {row.type}
                      {row.type === "Index Universal Life" && <Badge className="w-fit bg-primary text-primary-foreground text-[10px] font-bold font-mono uppercase tracking-widest px-2 py-0.5 shadow-[0_0_15px_rgba(255,191,0,0.3)]">
                          Top Choice
                        </Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="py-8 text-muted-foreground font-mono text-sm">
                    {row.duration}
                  </TableCell>
                  <TableCell className="py-8">
                    <Badge variant={row.costVariant} className={`px-3 py-1 font-mono text-xs ${row.costVariant === 'default' ? 'bg-primary text-primary-foreground' : 'text-foreground'}`}>
                      {row.cashValue}
                    </Badge>
                  </TableCell>
                  <TableCell className={`${i === 0 || i === 1 ? 'py-4' : 'py-8'} text-muted-foreground leading-relaxed font-sans text-sm whitespace-normal`}>
                    <ul className="list-disc pl-5 space-y-1">
                      {row.livingBenefits.split('\n').map((line, idx) => <li key={idx} data-index={idx} className="m-0">
                          {line.trim()}
                        </li>)}
                    </ul>
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </div>
      </div>
    </section>;
}